/**
 * SetImageModal — the ONE set-image flow, reused by the fleet-card tap and by
 * the entity Settings screen. FT-436 (2026-08-08).
 *
 * Two ways to set the image:
 *   (a) UPLOAD A PHOTO   — file picker → imageCompression → save (synced).
 *   (b) PICK A PLACEHOLDER — built-in graphic set (vessels: boat) → same
 *       compression → save (synced).
 *
 * PERMISSION: the modal is only opened by permission-passing surfaces, but it
 * ALSO defends itself — if `canSet` is false it renders a read-only notice and
 * hides every mutate control (no bypass even if opened programmatically).
 *
 * Compression + sync are entirely inside useEntityProfilePhoto — this component
 * is presentation + wiring only.
 */

import React, { useRef } from 'react';
import { X, Upload, ImagePlus, Loader2, Check } from 'lucide-react';
import { useEntityProfilePhoto } from './useEntityProfilePhoto';
import { useCanSetEntityImage } from './useCanSetEntityImage';
import { placeholdersFor } from './entityProfilePhoto';
import { isPhotoTooLargeError } from '../../../utils/photoPayloadBudget';

const SetImageModal = ({ isOpen, onClose, entityKind, entityId, entityName, capturedBy }) => {
  const fileRef = useRef(null);
  const canSet = useCanSetEntityImage(entityKind, entityId);
  const {
    photoUrl,
    saving,
    hasPhoto,
    setUploadedImage,
    setPlaceholderImage,
  } = useEntityProfilePhoto(entityId, capturedBy);

  if (!isOpen) return null;

  const placeholders = placeholdersFor(entityKind);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so re-picking the same file re-fires onChange.
    if (fileRef.current) fileRef.current.value = '';
    if (!file || !canSet) return;
    try {
      await setUploadedImage(file);
      onClose && onClose();
    } catch (err) {
      // Surface a minimal alert; keep the modal open for a retry. FT-577: a
      // photo that can't be made small enough to upload gets its own
      // plain-English explanation (it was NOT saved).
      // eslint-disable-next-line no-alert
      alert(isPhotoTooLargeError(err)
        ? err.message
        : 'Could not process that image. Please try a smaller photo.');
    }
  };

  const handlePlaceholder = async (key) => {
    if (!canSet) return;
    try {
      await setPlaceholderImage(key);
      onClose && onClose();
    } catch (_) {
      // eslint-disable-next-line no-alert
      alert('Could not set that placeholder. Please try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Set image"
      data-testid="set-image-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900 truncate">
            {hasPhoto ? 'Change image' : 'Set image'}{entityName ? ` — ${entityName}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
            aria-label="Close"
            data-testid="set-image-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Current / preview */}
          <div className="w-full h-32 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
            {photoUrl ? (
              <img src={photoUrl} alt="Current" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-slate-400">No image yet</span>
            )}
          </div>

          {!canSet ? (
            <p className="text-sm text-gray-500" data-testid="set-image-readonly">
              You can view this image. Only a skipper or an administrator can change it.
            </p>
          ) : (
            <>
              {/* (a) Upload */}
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="set-image-file-input"
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => fileRef.current && fileRef.current.click()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
                  data-testid="set-image-upload-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload a photo
                </button>
              </div>

              {/* (b) Placeholders */}
              {placeholders.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                    <ImagePlus className="w-3.5 h-3.5" /> Or pick a placeholder
                  </p>
                  <div className="grid grid-cols-4 gap-2" data-testid="set-image-placeholders">
                    {placeholders.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        disabled={saving}
                        onClick={() => handlePlaceholder(p.key)}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-cyan-400 disabled:opacity-50"
                        title={p.label}
                        aria-label={p.label}
                        data-testid={`set-image-placeholder-${p.key}`}
                      >
                        <img src={p.dataUrl} alt={p.label} className="w-full h-full object-cover" />
                        <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-cyan-600/70">
                          <Check className="w-4 h-4 text-white" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetImageModal;
